import { useEffect, useRef, useState } from 'react';
import MarkdownRenderer from '../MarkdownRenderer';

const POLL_MS = 250;

const widgetRenderer = () => {
  const renderTo = window.LAWMAai?.renderTo;
  return typeof renderTo === 'function' ? renderTo : null;
};

type Renderer = 'widget' | 'basic' | 'failed';

/**
 * Markdown shown by the widget's own renderer (LAWMAai.renderTo) when it's available, otherwise by
 * MarkdownRenderer. renderTo exists once the widget has started, so a preview opened before then
 * shows MarkdownRenderer and switches to the widget's renderer as soon as it's ready.
 */
export default function WidgetMarkdownPreview({ value }: { value: string }) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [renderer, setRenderer] = useState<Renderer>(() => (widgetRenderer() ? 'widget' : 'basic'));

  useEffect(() => {
    if (renderer !== 'basic') return;
    const poll = setInterval(() => {
      if (widgetRenderer()) setRenderer('widget');
    }, POLL_MS);
    return () => clearInterval(poll);
  }, [renderer]);

  useEffect(() => {
    if (renderer !== 'widget') return;
    const renderTo = widgetRenderer();
    // The widget restarted (its settings changed): the basic renderer until it's back.
    if (!renderTo || !targetRef.current) {
      setRenderer('basic');
      return;
    }
    try {
      renderTo(value, targetRef.current);
    } catch {
      setRenderer('failed');
    }
  }, [renderer, value]);

  if (renderer === 'widget') return <div ref={targetRef} />;
  return <MarkdownRenderer content={value} className="text-sm" imageSize="small" />;
}
